// A8 - Medical mocks for pilot health, exams, fatigue, certificates

export type MedicalStatus = 'fit' | 'fit-with-limit' | 'temp-unfit' | 'permanent-unfit' | 'pending'
export type ExamKind = 'annual' | 'semiannual' | 'pre-flight' | 'post-incident' | 'special'

export interface PilotMedicalProfile {
  id: string
  pilotCode: string
  pilotName: string
  rank: string
  pilotClass: string
  unitCode: string
  bloodType: string
  height: number
  weight: number
  bmi: number
  status: MedicalStatus
  certificateNumber: string
  certificateValidTo: string
  lastExamDate: string
  nextExamDate: string
  daysToExpiry: number
  fatigueScore: number
  stressScore: number
  flightHoursLast30Days: number
  flightHoursLast7Days: number
  notes?: string
}

export interface MedicalExam {
  id: string
  pilotCode: string
  pilotName: string
  date: string
  kind: ExamKind
  doctor: string
  vitalsBP: string
  vitalsHR: number
  vitalsSpO2: number
  vision: string
  hearing: string
  ecg: 'normal' | 'abnormal' | 'borderline'
  bloodTest: 'pass' | 'pending' | 'fail'
  psychTest: 'pass' | 'borderline' | 'fail'
  result: MedicalStatus
  validTo?: string
  remarks: string
}

export interface FlightMedicalCertificate {
  id: string
  number: string
  pilotCode: string
  pilotName: string
  class: 'Class I' | 'Class II' | 'Class III'
  issuedDate: string
  validFrom: string
  validTo: string
  issuedByDoctor: string
  approvedByCommander: string
  status: 'active' | 'expired' | 'suspended' | 'revoked'
  limitations?: string
}

export interface FatigueRecord {
  pilotCode: string
  date: string
  sleepHours: number
  fatigueLevel: number
  stressLevel: number
  reactionMs: number
  source: 'self-report' | 'wearable' | 'reaction-test'
}

export interface MedicalIncident {
  id: string
  pilotCode: string
  pilotName: string
  ts: string
  kind: 'injury' | 'illness' | 'incident' | 'mental'
  severity: 'minor' | 'moderate' | 'serious' | 'critical'
  diagnosis: string
  status: 'active' | 'recovering' | 'resolved'
  daysOff: number
}

export interface PsychAssessment {
  pilotCode: string
  pilotName: string
  date: string
  ies: number
  phq9: number
  gad7: number
  cohesion: number
  cognition: number
  decisionMaking: number
  overall: 'normal' | 'attention' | 'concern' | 'critical'
}

export interface MedicalKitItem {
  code: string
  name: string
  category: 'first-aid' | 'oxygen' | 'medication' | 'survival'
  airportCode: string
  quantity: number
  expiryDate: string
  status: 'ok' | 'low' | 'expired'
}

const PILOT_LIST = [
  ['PC-2024-001', 'Tr.tá Nguyễn Văn Hùng', 'Trung tá', 'Cấp 1', 'SD-371'],
  ['PC-2024-002', 'Th.tá Phạm Đức Tuấn', 'Thiếu tá', 'Cấp 1', 'SD-371'],
  ['PC-2024-003', 'Đ.uý Lê Quang Sơn', 'Đại uý', 'Cấp 2', 'SD-372'],
  ['PC-2024-004', 'Th.uý Vũ Hoàng Long', 'Thượng uý', 'Cấp 2', 'SD-372'],
  ['PC-2024-005', 'Tr.uý Bùi Thanh Tùng', 'Trung uý', 'Cấp 3', 'SD-370'],
  ['PC-2024-006', 'Đ.tá Trần Văn Hải', 'Đại tá', 'Cấp 1', 'SD-370'],
  ['PC-2024-007', 'Tr.tá Hoàng Anh Nam', 'Trung tá', 'Cấp 1', 'SD-371'],
  ['PC-2024-008', 'Th.tá Đặng Bá Phong', 'Thiếu tá', 'Cấp 2', 'SD-372'],
  ['PC-2024-009', 'Đ.uý Đỗ Trung Bình', 'Đại uý', 'Cấp 2', 'SD-371'],
  ['PC-2024-010', 'Th.uý Phan Hữu Khôi', 'Thượng uý', 'Cấp 3', 'SD-370'],
  ['PC-2024-011', 'Tr.uý Hồ Việt Cường', 'Trung uý', 'Cấp 3', 'SD-372'],
  ['PC-2024-012', 'Đ.uý Nguyễn Đức Đạt', 'Đại uý', 'Cấp 2', 'SD-370'],
  ['PC-2024-013', 'Th.uý Trần Quang Thắng', 'Thượng uý', 'Cấp 2', 'SD-371'],
  ['PC-2024-014', 'Tr.uý Lê Xuân Lâm', 'Trung uý', 'Cấp 3', 'SD-372'],
  ['PC-2024-015', 'Th.tá Phạm Bá Vinh', 'Thiếu tá', 'Cấp 1', 'SD-370'],
]

const STATUS_PATTERN: MedicalStatus[] = ['fit', 'fit', 'fit', 'fit', 'fit-with-limit', 'fit', 'fit', 'temp-unfit', 'fit', 'fit', 'fit', 'pending', 'fit', 'fit', 'fit-with-limit']

export const PILOT_MEDICAL_PROFILES: PilotMedicalProfile[] = PILOT_LIST.map(([code, name, rank, cls, unit], i) => {
  const lastExam = new Date(2026, 4 - (i % 6), 1 + ((i * 3) % 27))
  const nextExam = new Date(lastExam)
  nextExam.setMonth(nextExam.getMonth() + 6)
  const daysToExpiry = Math.floor((nextExam.getTime() - new Date('2026-05-06').getTime()) / 86400000)
  return {
    id: `mp-${i + 1}`,
    pilotCode: code,
    pilotName: name,
    rank,
    pilotClass: cls,
    unitCode: unit,
    bloodType: ['O+', 'A+', 'B+', 'AB+', 'O-'][i % 5],
    height: 168 + (i % 14),
    weight: 65 + (i % 18),
    bmi: 0,
    status: STATUS_PATTERN[i % STATUS_PATTERN.length],
    certificateNumber: `GP-YT-${2025 + (i % 2)}-${String(i + 1).padStart(4, '0')}`,
    certificateValidTo: nextExam.toISOString().slice(0, 10),
    lastExamDate: lastExam.toISOString().slice(0, 10),
    nextExamDate: nextExam.toISOString().slice(0, 10),
    daysToExpiry,
    fatigueScore: 20 + ((i * 13) % 60),
    stressScore: 15 + ((i * 17) % 55),
    flightHoursLast30Days: 18 + (i * 3) % 50,
    flightHoursLast7Days: 4 + (i % 14),
    notes: i === 7 ? 'Tạm dừng bay sau khám tâm lý sự cố INC-2026-0089' : i === 4 ? 'Hạn chế bay đêm 6 tháng' : undefined,
  }
}).map((p) => ({ ...p, bmi: +(p.weight / Math.pow(p.height / 100, 2)).toFixed(1) }))

const EXAM_KINDS: ExamKind[] = ['annual', 'semiannual', 'pre-flight', 'post-incident', 'special']
export const MEDICAL_EXAMS: MedicalExam[] = Array.from({ length: 24 }, (_, i) => {
  const pilot = PILOT_MEDICAL_PROFILES[i % PILOT_MEDICAL_PROFILES.length]
  const ecgRoll = i % 7
  return {
    id: `ex-${i + 1}`,
    pilotCode: pilot.pilotCode,
    pilotName: pilot.pilotName,
    date: `2026-${String(((i % 5) + 1)).padStart(2, '0')}-${String(((i * 3) % 27) + 1).padStart(2, '0')}`,
    kind: EXAM_KINDS[i % 5],
    doctor: ['BS Nguyễn V.A', 'BS Trần T.B', 'BS Lê V.C', 'BS Phạm T.D'][i % 4],
    vitalsBP: `${110 + (i % 25)}/${70 + (i % 15)}`,
    vitalsHR: 60 + (i % 25),
    vitalsSpO2: 96 + (i % 4),
    vision: i % 8 === 0 ? '8/10' : '10/10',
    hearing: i % 11 === 0 ? 'Suy giảm nhẹ tần số cao' : 'Bình thường',
    ecg: ecgRoll === 0 ? 'borderline' : ecgRoll === 1 ? 'abnormal' : 'normal',
    bloodTest: i === 7 || i === 14 ? 'pending' : 'pass',
    psychTest: i === 7 ? 'fail' : i === 4 ? 'borderline' : 'pass',
    result: i === 7 ? 'temp-unfit' : i === 4 ? 'fit-with-limit' : 'fit',
    validTo: `2026-${String(((i % 5) + 7)).padStart(2, '0')}-${String(((i * 3) % 27) + 1).padStart(2, '0')}`,
    remarks: i === 7 ? 'Đề nghị nghỉ bay 90 ngày + đánh giá lại' : i === 4 ? 'Hạn chế bay đêm' : 'Đủ điều kiện bay đầy đủ',
  }
})

export const FLIGHT_MEDICAL_CERTIFICATES: FlightMedicalCertificate[] = PILOT_MEDICAL_PROFILES.map((p, i) => ({
  id: `cert-${i + 1}`,
  number: p.certificateNumber,
  pilotCode: p.pilotCode,
  pilotName: p.pilotName,
  class: p.pilotClass.startsWith('Cấp 1') ? 'Class I' : p.pilotClass.startsWith('Cấp 2') ? 'Class II' : 'Class III',
  issuedDate: p.lastExamDate,
  validFrom: p.lastExamDate,
  validTo: p.certificateValidTo,
  issuedByDoctor: 'BS CK1 Nguyễn Văn Hà — Phó CN Quân y HK',
  approvedByCommander: 'Đại tá Lê Q.M — Tư lệnh Sư đoàn',
  status: p.status === 'temp-unfit' ? 'suspended' : p.daysToExpiry < 0 ? 'expired' : 'active',
  limitations: p.notes,
}))

export const FATIGUE_RECORDS: FatigueRecord[] = PILOT_MEDICAL_PROFILES.flatMap((p, pi) =>
  Array.from({ length: 14 }, (_, di) => ({
    pilotCode: p.pilotCode,
    date: `2026-04-${String(22 + di).padStart(2, '0')}`,
    sleepHours: 5 + ((pi + di) % 4),
    fatigueLevel: Math.max(0, Math.min(100, p.fatigueScore + ((di * 7) % 20) - 10)),
    stressLevel: Math.max(0, Math.min(100, p.stressScore + ((di * 11) % 25) - 12)),
    reactionMs: 240 + ((pi + di * 3) % 80),
    source: di % 3 === 0 ? 'wearable' : di % 3 === 1 ? 'self-report' : 'reaction-test',
  })),
)

export const MEDICAL_INCIDENTS: MedicalIncident[] = [
  { id: 'inc-1', pilotCode: 'PC-2024-008', pilotName: 'Th.tá Đặng Bá Phong', ts: '2026-04-12 14:20', kind: 'incident', severity: 'serious', diagnosis: 'Stress sau sự cố INC-2026-0089', status: 'recovering', daysOff: 38 },
  { id: 'inc-2', pilotCode: 'PC-2024-005', pilotName: 'Tr.uý Bùi Thanh Tùng', ts: '2026-03-22 09:10', kind: 'injury', severity: 'minor', diagnosis: 'Bong gân cổ chân khi tập thể dục', status: 'resolved', daysOff: 7 },
  { id: 'inc-3', pilotCode: 'PC-2024-002', pilotName: 'Th.tá Phạm Đức Tuấn', ts: '2026-02-15 11:30', kind: 'illness', severity: 'moderate', diagnosis: 'Cúm A — sốt cao 39°C', status: 'resolved', daysOff: 10 },
  { id: 'inc-4', pilotCode: 'PC-2024-014', pilotName: 'Tr.uý Lê Xuân Lâm', ts: '2026-04-28 16:45', kind: 'mental', severity: 'minor', diagnosis: 'Lo âu nhẹ trước thi nâng cấp', status: 'recovering', daysOff: 0 },
  { id: 'inc-5', pilotCode: 'PC-2024-011', pilotName: 'Tr.uý Hồ Việt Cường', ts: '2026-01-08 08:15', kind: 'illness', severity: 'minor', diagnosis: 'Viêm họng cấp', status: 'resolved', daysOff: 4 },
]

export const PSYCH_ASSESSMENTS: PsychAssessment[] = PILOT_MEDICAL_PROFILES.slice(0, 12).map((p, i) => ({
  pilotCode: p.pilotCode,
  pilotName: p.pilotName,
  date: `2026-${String(((i % 5) + 1)).padStart(2, '0')}-${String(((i * 5) % 27) + 1).padStart(2, '0')}`,
  ies: i === 7 ? 42 : 8 + ((i * 7) % 18),
  phq9: i === 7 ? 16 : 2 + ((i * 5) % 8),
  gad7: i === 7 ? 14 : 1 + ((i * 7) % 7),
  cohesion: 70 + ((i * 11) % 25),
  cognition: 75 + ((i * 13) % 22),
  decisionMaking: 78 + ((i * 17) % 18),
  overall: i === 7 ? 'concern' : i === 4 ? 'attention' : 'normal',
}))

export const MEDICAL_KIT_ITEMS: MedicalKitItem[] = [
  { code: 'KIT-FA-001', name: 'Bông băng cứu thương loại A', category: 'first-aid', airportCode: 'NBA', quantity: 240, expiryDate: '2027-06-30', status: 'ok' },
  { code: 'KIT-FA-002', name: 'Garô cầm máu tự động', category: 'first-aid', airportCode: 'NBA', quantity: 18, expiryDate: '2028-12-31', status: 'ok' },
  { code: 'KIT-OX-001', name: 'Bình oxy y tế cá nhân 2L', category: 'oxygen', airportCode: 'NBA', quantity: 24, expiryDate: '2029-03-15', status: 'ok' },
  { code: 'KIT-OX-002', name: 'Mặt nạ oxy phi công', category: 'oxygen', airportCode: 'NBA', quantity: 8, expiryDate: '2027-09-20', status: 'low' },
  { code: 'KIT-MD-001', name: 'Adrenaline 1mg/ml', category: 'medication', airportCode: 'NBA', quantity: 30, expiryDate: '2026-08-15', status: 'ok' },
  { code: 'KIT-MD-002', name: 'Atropine 1mg/ml', category: 'medication', airportCode: 'NBA', quantity: 12, expiryDate: '2026-04-30', status: 'expired' },
  { code: 'KIT-SV-001', name: 'Áo phao SOLAS', category: 'survival', airportCode: 'NBA', quantity: 60, expiryDate: '2028-01-31', status: 'ok' },
  { code: 'KIT-SV-002', name: 'Bộ sinh tồn rừng nhiệt đới', category: 'survival', airportCode: 'DAD', quantity: 4, expiryDate: '2030-12-31', status: 'low' },
  { code: 'KIT-FA-003', name: 'Băng ép cá nhân', category: 'first-aid', airportCode: 'DAD', quantity: 180, expiryDate: '2027-11-30', status: 'ok' },
  { code: 'KIT-MD-003', name: 'Thuốc giảm đau Morphine 10mg', category: 'medication', airportCode: 'BHA', quantity: 24, expiryDate: '2026-09-30', status: 'ok' },
]

export const MEDICAL_KPIS = {
  totalPilots: PILOT_MEDICAL_PROFILES.length,
  fitPilots: PILOT_MEDICAL_PROFILES.filter((p) => p.status === 'fit').length,
  fitWithLimit: PILOT_MEDICAL_PROFILES.filter((p) => p.status === 'fit-with-limit').length,
  tempUnfit: PILOT_MEDICAL_PROFILES.filter((p) => p.status === 'temp-unfit').length,
  pending: PILOT_MEDICAL_PROFILES.filter((p) => p.status === 'pending').length,
  expiringSoon: PILOT_MEDICAL_PROFILES.filter((p) => p.daysToExpiry <= 30 && p.daysToExpiry > 0).length,
  expired: PILOT_MEDICAL_PROFILES.filter((p) => p.daysToExpiry < 0).length,
  highFatigue: PILOT_MEDICAL_PROFILES.filter((p) => p.fatigueScore > 60).length,
}
